import { z } from 'zod';
import { SubscriberRelationshipSchema } from './insurance.js';
import { SubmissionStatusSchema } from './intake.js';
import { ConsentRecordSchema, NotificationChannelSchema } from './notifications.js';
import { PAGE_LIMIT_DEFAULT, PAGE_LIMIT_MAX, PageInfoSchema } from './pagination.js';
import { PaymentMethodSummarySchema } from './payments.js';
import { AppointmentStatusSchema, LocalDateSchema, StateCodeSchema, TimeZoneSchema, } from './scheduling.js';
/**
 * The patient record, as staff see it (M11.3, FR-ADMIN-04, `API.md` §2.6).
 *
 * This file is the promoted copy of `apps/admin/src/features/patients/types.ts`, which
 * the admin UI wrote against `DATABASE.md` §7.4 while the endpoints did not exist
 * (`ADMIN_OPERATIONS_UI.md` §2.1). Field names are unchanged wherever the UI had one, so
 * the seam's move is an import swap; the three places they differ are listed in the
 * milestone report and marked below.
 *
 * ## What is deliberately not here
 *
 * No clinical anything. A patient record screen shows who somebody is, how to reach
 * them, what they have booked, which payer they named and which messages went out. The
 * *answers* they gave on a form are the intake module's, read through
 * `GET /admin/appointments/:id/intake`, which audits the read - and a summary row here
 * would be a way to see paperwork without leaving that trace.
 *
 * ## The member id is four digits, and that is the whole of it
 *
 * `insurance_policies.member_id_enc` is decryptable by this actor, and the record screen
 * still gets `memberIdLast4`. A policy card that renders the full member id turns every
 * shoulder-surfer in a clinic reception into a data breach, and nothing on this screen
 * needs the other digits: the eligibility check is run server-side from the stored value.
 */
/* ------------------------------------------------------------------- the row ---- */
/** `patients.status`. `anonymized` is the end state of the ADR-021 workflow. */
export const PATIENT_STATUSES = ['active', 'inactive', 'anonymized'];
export const PatientStatusSchema = z.enum(PATIENT_STATUSES);
/**
 * The editable contact block (`PATCH /admin/patients/:id`).
 *
 * The mailing address columns landed with this milestone
 * (`ADMIN_OPERATIONS_UI.md` §6 item 5): `patients` had `state` and `time_zone` and
 * nothing else, so the contact card held an address the server could not store.
 *
 * Every field but `state` is nullable, because a clinic books a patient from a phone
 * call with an email and a state and nothing more, and a card that refuses to save
 * until somebody invents a postcode is a card staff work around on paper.
 */
export const PatientContactSchema = z
    .object({
    email: z.email(),
    /** E.164, or null. */
    phone: z
        .string()
        .regex(/^\+[1-9]\d{7,14}$/, 'Enter a phone number in international format.')
        .nullable(),
    addressLine1: z.string().max(200).nullable(),
    addressLine2: z.string().max(200).nullable(),
    city: z.string().max(120).nullable(),
    /** State of residence. It decides which clinicians may see this patient. */
    state: StateCodeSchema,
    /** US ZIP or ZIP+4. */
    postalCode: z
        .string()
        .regex(/^\d{5}(-\d{4})?$/, 'Enter a 5-digit ZIP code.')
        .nullable(),
})
    .strict();
/** A row of `GET /admin/patients` (API.md §2.6). */
export const AdminPatientSchema = z
    .object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    /** `firstName lastName`, rendered once by the server so forty rows agree. */
    displayName: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    state: z.string(),
    dateOfBirth: LocalDateSchema,
    status: PatientStatusSchema,
    /** Null when nothing is booked ahead. Drives the "has upcoming" filter. */
    nextAppointmentAt: z.iso.datetime().nullable(),
    hasCardOnFile: z.boolean(),
    createdAt: z.iso.datetime(),
    /** Set once the ADR-021 workflow has run. The row stays; the person is gone. */
    anonymizedAt: z.iso.datetime().nullable(),
})
    .strict();
/* --------------------------------------------------------------- the record ---- */
/** One of the patient's visits, as the record screen lists them. */
export const PatientAppointmentSummarySchema = z
    .object({
    id: z.string(),
    bookingReference: z.string(),
    startAt: z.iso.datetime(),
    endAt: z.iso.datetime(),
    status: AppointmentStatusSchema,
    appointmentTypeName: z.string(),
    providerName: z.string(),
    clinicTimeZone: z.string(),
})
    .strict();
/** `insurance_policies`, narrowed to what a record screen may show. */
export const InsurancePolicySummarySchema = z
    .object({
    id: z.string(),
    payerName: z.string(),
    /** The full member id never reaches a browser. */
    memberIdLast4: z.string().nullable(),
    subscriberRelationship: SubscriberRelationshipSchema,
    isPrimary: z.boolean(),
    status: z.enum(['active', 'replaced', 'removed']),
    hasCardImages: z.boolean(),
    createdAt: z.iso.datetime(),
})
    .strict();
export const PatientIntakeSubmissionSchema = z
    .object({
    id: z.string(),
    formName: z.string(),
    formVersion: z.int(),
    status: SubmissionStatusSchema,
    submittedAt: z.iso.datetime().nullable(),
    appointmentId: z.string().nullable(),
})
    .strict();
export const PatientNotificationSummarySchema = z
    .object({
    id: z.string(),
    kind: z.string(),
    channel: NotificationChannelSchema,
    status: z.string(),
    scheduledFor: z.iso.datetime().nullable(),
    sentAt: z.iso.datetime().nullable(),
    errorClass: z.string().nullable(),
    appointmentId: z.string().nullable(),
})
    .strict();
/**
 * `consents` (DATABASE.md §7.4). Immutable: the card only ever lists them.
 *
 * The row's `id` is what this adds to M9's `ConsentRecord`, which the patient portal
 * reads as a list of facts and never needs to address one of. The staff card does: an
 * auditor asking "which acceptance is this" wants the row, not the kind.
 */
export const PatientConsentSchema = ConsentRecordSchema.extend({ id: z.string() });
/**
 * What the deletion card shows once the workflow has run (ADR-021).
 *
 * `status` is `completed` and nothing else, because this platform's anonymization is one
 * call: `POST /admin/patients/:id/anonymize` tombstones the record inside a transaction
 * or refuses. The UI's mock modelled a `pending` request awaiting approval; the approval
 * is the step-up and the second permission key, which happen *before* the call rather
 * than after it. The literal is kept as an enum so a later two-stage workflow is an
 * additive value rather than a type change.
 */
export const AnonymizationRecordSchema = z
    .object({
    status: z.enum(['completed']),
    reason: z.string(),
    /** The support ticket the request came from. Required, for the audit trail. */
    ticketRef: z.string(),
    /** The administrator who approved it. */
    requestedBy: z.string().nullable(),
    completedAt: z.iso.datetime(),
})
    .strict();
/** `GET /admin/patients/:id` (API.md §2.6). */
export const AdminPatientDetailSchema = z
    .object({
    patient: AdminPatientSchema.extend({
        timeZone: TimeZoneSchema,
        preferredLanguage: z.string(),
        sex: z.enum(['female', 'male', 'other', 'unknown']).nullable(),
        contact: PatientContactSchema,
        /** Tebra's `PatientID`, when this clinic has an EHR. */
        ehrExternalId: z.string().nullable(),
    }),
    /** Newest first, capped. The full history is the appointment list filtered. */
    appointments: z.array(PatientAppointmentSummarySchema),
    policies: z.array(InsurancePolicySummarySchema),
    paymentMethods: z.array(PaymentMethodSummarySchema),
    intakeSubmissions: z.array(PatientIntakeSubmissionSchema),
    notifications: z.array(PatientNotificationSummarySchema),
    consents: z.array(PatientConsentSchema),
    /** Null until the record has been anonymized. */
    anonymization: AnonymizationRecordSchema.nullable(),
})
    .strict();
/* ------------------------------------------------------------------ queries ---- */
export const PATIENT_SORTS = ['lastName', '-lastName', 'createdAt', '-createdAt'];
export const PatientSortSchema = z.enum(PATIENT_SORTS);
/**
 * `GET /admin/patients` (API.md §1.7, §2.6).
 *
 * `q` searches `patients.search_key` - a generated, trigram-indexed column holding the
 * name, the email and the phone number. It is never logged (SECURITY.md §9: a search box
 * on a patient list is a stream of names).
 */
export const AdminPatientListQuerySchema = z
    .object({
    q: z.string().min(1).max(120).optional(),
    state: StateCodeSchema.optional(),
    status: PatientStatusSchema.optional(),
    /**
     * Patients with a visit ahead of now.
     *
     * `'true' | 'false'` rather than a coerced boolean, for the reason `feedback.ts`
     * gives: `z.coerce.boolean()` reads the string `"false"` as `true`, which is a
     * filter that silently does the opposite of what the checkbox says.
     */
    hasUpcoming: z.enum(['true', 'false']).optional(),
    hasCard: z.enum(['true', 'false']).optional(),
    sort: PatientSortSchema.default('lastName'),
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(PAGE_LIMIT_DEFAULT),
    cursor: z.string().min(1).optional(),
})
    .strict();
/**
 * `PATCH /admin/patients/:id`.
 *
 * Contact and preference fields only. A name, a date of birth and a state are identity:
 * changing them is a merge or a correction with its own workflow (`patients.merge`,
 * SECURITY.md §5.1), not a text field on a card - and `state` in particular decides
 * which clinicians may legally see the patient, so it moves through the contact block
 * where the audit row names it.
 */
export const UpdatePatientRequestSchema = z
    .object({
    email: z.email().optional(),
    phone: z
        .string()
        .regex(/^\+[1-9]\d{7,14}$/, 'Enter a phone number in international format.')
        .nullable()
        .optional(),
    addressLine1: z.string().max(200).nullable().optional(),
    addressLine2: z.string().max(200).nullable().optional(),
    city: z.string().max(120).nullable().optional(),
    state: StateCodeSchema.optional(),
    postalCode: z
        .string()
        .regex(/^\d{5}(-\d{4})?$/, 'Enter a 5-digit ZIP code.')
        .nullable()
        .optional(),
    timeZone: TimeZoneSchema.optional(),
    preferredLanguage: z.string().min(2).max(10).optional(),
})
    .strict()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Change at least one field.',
});
/**
 * `POST /admin/patients/:id/anonymize` (`patients.anonymize`, step-up, no-impersonation).
 *
 * Both fields are mandatory and neither has a default. A deletion that leaves no reason
 * behind is indistinguishable from a mistake six months later, and the ticket reference
 * is what ties the clinic's own record of the request to this one.
 */
export const AnonymizePatientRequestSchema = z
    .object({
    reason: z.string().min(10).max(500),
    ticketRef: z.string().min(1).max(80),
})
    .strict();
/* ---------------------------------------------------------------- envelopes ---- */
export const AdminPatientListResponseSchema = z
    .object({ data: z.array(AdminPatientSchema), page: PageInfoSchema })
    .strict();
export const AdminPatientDetailResponseSchema = z
    .object({ data: AdminPatientDetailSchema })
    .strict();
//# sourceMappingURL=patients.js.map